import { Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatDate } from '@/lib/format';
import type { CustomerDetail } from '@/api/customers';
import { industryLabel, sourceLabel, leadTypeLabel, miscLabel } from './label-maps';
import CustomerActionButtons from './customer-action-buttons';
import CustomerContactSection from './customer-contact-section';
import CustomerAddressSection from './customer-address-section';
import CustomerActivityTimeline from './customer-activity-timeline';
import { PinnedNotesSection } from './pinned-notes-section';
import { LeadActionsBar } from './lead-actions-bar';

/** Render a website value as a clickable external link (or em dash). */
function websiteValue(url?: string) {
  if (!url) return undefined;
  const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      style={{ color: 'var(--vn-primary-text)', overflowWrap: 'anywhere' }}
    >
      {url}
    </a>
  );
}

/** Narrowed accessor for the loosely-typed `company` record. */
function companyField(company: Record<string, unknown> | undefined, key: string): string | undefined {
  const v = company?.[key];
  return v == null ? undefined : String(v);
}

/**
 * Basic Information tab — customer actions (convert / delete / add contact /
 * add note / special-customer) over the company profile, a humanized overview,
 * the contact records (with masked email/phone reveal), address, and the
 * activity-history timeline. Composed from @ui Cards + shared sections.
 */
export function BasicInformationTab({ detail }: { detail: CustomerDetail }) {
  const company = detail.company;
  const hasCompany = company && Object.keys(company).length > 0;

  const companyItems: InfoItem[] = [
    { label: 'Company Name', value: companyField(company, 'company_name') },
    { label: 'Tax Code', value: companyField(company, 'company_tax') },
    { label: 'Industry', value: industryLabel(companyField(company, 'company_industry')) },
    { label: 'Website', value: websiteValue(companyField(company, 'company_website')) },
    { label: 'Established', value: formatDate(companyField(company, 'company_establish_date')) },
    { label: 'Address', value: companyField(company, 'company_address'), full: true },
  ];

  // Customer-level facts (email/phone live in the masked Contacts section below).
  const overviewItems: InfoItem[] = [
    { label: 'Type', value: miscLabel(detail.customer_type) },
    { label: 'Industry', value: industryLabel(detail.customer_industry) },
    { label: 'Source', value: sourceLabel(detail.customer_source) },
    { label: 'Lead Type', value: leadTypeLabel(detail.customer_lead_type) },
    { label: 'Website', value: websiteValue(detail.customer_website) },
  ];

  const isLead =
    detail.customer_status === 'cool-lead' ||
    detail.customer_status === 'qualified-prospect';

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <CustomerActionButtons customer={detail} />

      {/* Classify Lead + Unfollow Lead — only shown for lead-stage customers */}
      {isLead ? (
        <LeadActionsBar customerId={detail.customer_id} customerStatus={detail.customer_status} />
      ) : null}

      {hasCompany ? (
        <Card flat>
          <CardHeader>
            <CardTitle>Company Profile</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid items={companyItems} />
          </CardBody>
        </Card>
      ) : null}

      <Card flat>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardBody>
          <InfoGrid items={overviewItems} />
        </CardBody>
      </Card>

      <CustomerContactSection customerId={detail.customer_id} />

      <CustomerAddressSection customerId={detail.customer_id} />

      <PinnedNotesSection customerId={detail.customer_id} />

      <CustomerActivityTimeline customerId={detail.customer_id} />
    </div>
  );
}
